import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ExecutiveRefreshResponse, ExecutiveReportDto, ExecutiveReportListResponse, ExecutiveReportResponse, ExecutiveReportRowDto, ExecutiveThemeDto } from '../../shared/api-types.js';
import {
  EXECUTIVE_CONFIDENCES,
  EXECUTIVE_CONFIDENCE_LABELS,
  EXECUTIVE_PRIOR_VERDICT_LABELS,
  EXECUTIVE_REPORT_TRIGGER_LABELS,
  EXECUTIVE_THEME_KINDS,
  EXECUTIVE_THEME_KIND_LABELS,
  EXECUTIVE_TRENDS,
  EXECUTIVE_TREND_LABELS,
} from '../../shared/enums.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useExecutiveBadge } from '../shell/executive-badge.js';
import {
  Banner,
  Button,
  Chip,
  CopyButton,
  DataTable,
  EM_DASH,
  LinkButton,
  Page,
  PageHead,
  Section,
  StateView,
  StatusPill,
  failureDetail,
  formatCount,
  pluralise,
  sortOptionsFor,
  useNow,
  useRequest,
  useUrlSort,
  type DataTableColumn,
} from '../ui/index.js';
import { AuthorLine, ExecutiveFailure, ExecutiveStrip, RefreshNote, RefreshNow } from './executive-parts.js';
import {
  LOADING_ALL_REPORTS,
  NO_REPORT_TITLE,
  NO_SUGGESTED_ACTIONS,
  backfillProgressSentence,
  evidenceText,
  firstReportWaitSentence,
  lastFailureSentence,
  lastFailureTitle,
  leftOutSentence,
  noReportDescription,
  reportDocumentTitle,
  reportInSentence,
  reportName,
  reportsExtentText,
  resummaryStage,
  runningSentence,
  runningWord,
  slackBasisText,
  thinSentence,
  windowText,
  withheldReasonText,
  writtenFromSentence,
  type FailureContext,
} from './lib/executive.js';
import { useCursorPages, type CursorPage } from './lib/use-cursor-pages.js';
import { PriorityPill } from './todo-table.js';

// The Report section (ADR 0019; design §2.13 "Report"): a record page (06-modules §5) — R1 the identity header (what kind,
// which one, its state with a glyph, where it came from and when, the id as selectable text, and "Refresh now" with its
// cooldown stated), R2 its own fields (the headline, the themes as a sortable table, the pulse note, the verdicts on the
// report before it), and R4 the earlier reports. The latest readable report at `/executive`; one by its id at
// `/executive?report=<id>`, so a report is a place an executive can send a colleague (§5.5).
//
// What is shown is what the route returns: a stored report as written (its names not re-checked when read, "Re-check on
// writing"); while Projects are off, the named themes and what rests on them are not served, and the page says nothing of
// them ("Hide named themes"). Reading a report records the executive's first open of it on the server — the reason the
// shell's badge is told (`markOpened`).

/** How often a report being written is looked at again: a report takes minutes, and nothing is lost between looks. */
const RUNNING_POLL_MS = 15_000;
const EARLIER_PAGE = 30;
const SECTION_TITLE = 'Organisation report';
type Waiting = Extract<ExecutiveRefreshResponse, { state: 'waiting' }>;

/**
 * The Report section at its address: the latest report at `/executive`, one by its id at `/executive?report=<id>`.
 *
 * Why keyed by the address (the stage 6 review; 06-modules §5.3 R1, §5.5 and §5.8): opening another report — an earlier
 * one, "Compared with", or the "Latest report" crumb — changes `?report=` alone, and a section kept mounted across it kept
 * the previous report's data while the next loaded (`useRequest` keeps data across a change of its key), so for a moment
 * the previous report stood under the new address with the new crumb — and for good, with no error, if that read failed
 * (a report retention took, a grant revoked, a dropped connection). Keyed, each address starts from loading with a
 * request of its own, and its failure is its own. A change of report is a change of page: `RouteFocus` moves focus to the
 * heading and the scroll to the top (`RECORD_PARAMS`, src/client/paths.ts; 02-patterns §4).
 */
export function ReportSection() {
  const [params] = useSearchParams();
  const reportId = params.get('report');
  return <ReportAt key={reportId === null ? 'latest' : `report:${reportId}`} reportId={reportId} />;
}

/**
 * Why a crumb only for a report opened by its id: the latest report is the module's own page, with no ancestry to show
 * (02-patterns §2.1: "where it has none, draw no crumb"); an earlier one is a detail of it, and on a phone the crumb is
 * the back affordance a detail view must carry.
 */
const LATEST_CRUMB = [{ label: 'Latest report', to: PATHS.executive, role: 'module' as const }];

function ReportAt({ reportId }: { reportId: string | null }) {
  const badge = useExecutiveBadge();
  const now = useNow();
  const request = useRequest<ExecutiveReportResponse>(
    (signal) => api.get<ExecutiveReportResponse>(reportId ? `/api/executive/reports/${encodeURIComponent(reportId)}` : '/api/executive/report', { signal }),
    [reportId],
  );
  const [waiting, setWaiting] = useState<Waiting | null>(null);
  const data = request.data;
  const byId = reportId !== null;
  // Why a failure is shown whenever the current read failed, data or not (the stage 6 review; 03-components §1, 06-modules
  // §5.8): a later read of this address — the look again while a report is written, or after "Refresh now" — that fails
  // must not leave what was read before standing as though it were current.
  const failed = request.status === 'error' && request.error !== null;
  const report = failed ? null : (data?.report ?? null);

  // The server recorded this executive's open of the report with the read that returned it: the badge stops counting it.
  const { markOpened } = badge;
  useEffect(() => {
    if (report) markOpened({ id: report.id, windowEnd: report.windowEnd });
  }, [report, markOpened]);

  // A report being written is looked at again until it lands (the same read: its open is recorded once, and nothing more).
  const reload = request.reload;
  useEffect(() => {
    if (failed || !data?.running) return;
    const timer = window.setTimeout(reload, RUNNING_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [data, failed, reload]);

  // --- Identity kept in view while the reader scrolls (06-modules §5.3 R1), as the meeting record keeps its own ---------
  const headRef = useRef<HTMLDivElement | null>(null);
  const [condensed, setCondensed] = useState(false);
  const shownId = report?.id ?? null;
  useEffect(() => {
    const head = headRef.current;
    if (!head || shownId === null) {
      setCondensed(false);
      return;
    }
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const bar = document.querySelector<HTMLElement>('.top-bar');
      const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
      setCondensed(head.getBoundingClientRect().bottom < barBottom);
    };
    const schedule = (): void => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [shownId]);

  const onAnswered = (answer: ExecutiveRefreshResponse): void => {
    if (answer.state === 'waiting') {
      setWaiting(answer);
      return;
    }
    setWaiting(null);
    reload();
  };

  const name = report ? reportName(report) : null;
  const pill = report ? <ReportPill report={report} /> : null;
  const refresh = data && !failed ? <RefreshNow cooldownUntil={data.cooldownUntil} running={data.running !== null} onAnswered={onAnswered} /> : undefined;
  // Why the failure banner says which page it is on (the stage 6 review): the route sends the latest failure whenever it is
  // newer than the report shown — on an earlier report opened by its id, and with no report at all, too.
  const failureContext: FailureContext = byId ? 'byId' : report ? 'latest' : 'none';

  const notices =
    data && !failed ? (
      <>
        <RefreshNote cooldownUntil={data.cooldownUntil} now={now} />
        {data.running ? (
          <Banner tone="info" icon="clock" title={runningWord(data.running.status)}>{runningSentence(data.running, now)}</Banner>
        ) : null}
        {waiting ? (
          <Banner tone="info" icon="clock" title="Waiting for the re-summary" onDismiss={() => setWaiting(null)}>
            {`${firstReportWaitSentence(waiting.until, resummaryStage(waiting.backfill), now)} ${backfillProgressSentence(waiting.backfill)}`}
          </Banner>
        ) : null}
        {data.lastFailure ? (
          <Banner tone="warning" title={lastFailureTitle(failureContext)}>{lastFailureSentence(data.lastFailure, failureContext, now)}</Banner>
        ) : null}
      </>
    ) : null;

  let body: ReactNode;
  if (failed && request.error) {
    // Why the way back names the latest report: an address that names a report retention took, or a mistyped one, still
    // has the module's own record to land on (06-modules §5.5: land on the record and say so).
    body = <ExecutiveFailure failure={request.error} what="the report" onRetry={reload} backTo={byId ? { to: PATHS.executive, label: 'Go to the latest report' } : undefined} />;
  } else if (!data) {
    body = <StateView kind="loading" label="Loading the report" minHeight="24rem" />;
  } else if (!report) {
    // Why the wait and its time (the owner's ruling of 2026-10-09 on ADR 0019's Open 44, "First report's time shown"): the route carries the first
    // report's wait, so it is said here as Coverage says it — never `nextScheduledAt`, the moment of the read while none exists.
    body = (
      <StateView
        kind="empty"
        icon="clock"
        title={NO_REPORT_TITLE}
        description={noReportDescription(data.running !== null, data.firstReport, now)}
        action={<LinkButton to={PATHS.executiveCoverage} variant="secondary" size="sm">Open Coverage</LinkButton>}
        minHeight="16rem"
      />
    );
  } else {
    body = <ReportBody report={report} />;
  }

  // Why one page head for every state, in one place (the stage 6 review; 02-patterns §4): the heading `RouteFocus` focuses
  // on arrival is the loading state's; were the record's head another element, focus would fall to the page's body the
  // moment the report landed. Kept in one place, it is the same heading, renamed.
  return (
    <Page measure="read-wide" className={report ? 'record-page executive-report' : undefined}>
      <div ref={headRef}>
        <PageHead
          title={name ?? SECTION_TITLE}
          crumbs={byId && !failed ? LATEST_CRUMB : undefined}
          documentTitle={report ? reportDocumentTitle(report) : undefined}
          titleAside={pill ?? undefined}
          identifier={
            report ? (
              <span className="record-identifier">
                <span>{SECTION_TITLE}</span>
                <code className="selectable">{report.id}</code>
                <CopyButton value={report.id} label="Copy report id" />
              </span>
            ) : undefined
          }
          subtitle={
            report ? (
              <>
                {writtenFromSentence(report)}
                {request.refreshing ? <span className="visually-hidden"> (checking for a newer report)</span> : null}
              </>
            ) : undefined
          }
          actions={refresh}
          strip={<ExecutiveStrip />}
        />
      </div>
      {report ? (
        <div className="record-sticky" hidden={!condensed} aria-hidden="true">
          <span className="record-sticky__kind">{SECTION_TITLE}</span>
          <span className="record-sticky__title">{name}</span>
          <span className="record-sticky__pill">{pill}</span>
        </div>
      ) : null}
      {notices}
      {body}
      <AuthorLine />
    </Page>
  );
}

/**
 * The record's state with its glyph: Completed, or Skipped with its reason (05-content §6). Why the reason its executor
 * recorded (the owner's ruling of 2026-10-13 on ADR 0019's Open 50, "Record the reasons"): the page states the cause. A
 * report withheld before the reason was kept reads a minimum since removed where it held back counts (the owner's ruling of
 * 2026-10-12, "Remove them everywhere"), else that its reason was not recorded — and so does a withheld record the route
 * served without its counts, whose cause nothing tells.
 */
function ReportPill({ report }: { report: ExecutiveReportDto }) {
  if (report.status !== 'insufficient') return <StatusPill tone="success">Completed</StatusPill>;
  return (
    <span className="cluster">
      <StatusPill tone="neutral" icon="minus">Skipped</StatusPill>
      <span className="text-sm secondary">{withheldReasonText(report.insufficient ?? { people: 0, meetings: 0, reason: null })}</span>
    </span>
  );
}

/** R2–R4: the record's own fields, its themes, actions and verdicts, and the earlier reports. */
function ReportBody({ report }: { report: ExecutiveReportDto }) {
  const thin = report.status === 'insufficient';
  return (
    <>
      {/* R2 — the record's own fields. */}
      <section className="executive-fields stack stack--3" aria-labelledby="report-fields-heading">
        <h2 className="visually-hidden" id="report-fields-heading">About this report</h2>
        {thin && report.insufficient ? <p className="executive-headline" data-thin-report="">{thinSentence(report.insufficient)}</p> : null}
        {!thin && report.headline ? <p className="executive-headline">{report.headline}</p> : null}
        <dl className="field-list">
          <dt>Window</dt>
          <dd>{windowText(report.windowStart, report.windowEnd)}</dd>
          <dt>Began</dt>
          <dd>{EXECUTIVE_REPORT_TRIGGER_LABELS[report.trigger]}</dd>
          {report.basis ? (
            <>
              <dt>Meetings read</dt>
              <dd className="num">{formatCount(report.basis.meetings)}</dd>
              <dt>People</dt>
              <dd className="num">{formatCount(report.basis.people)}</dd>
              <dt>Members without AI summaries</dt>
              <dd className="num">{formatCount(report.basis.withoutGrant)}</dd>
              <dt>Slack</dt>
              <dd>{slackBasisText(report.basis.slack)}</dd>
            </>
          ) : null}
          {/* Why a withheld report's Slack too (the owner's ruling of 2026-10-12, "Count whoever is connected"): a window with
              no meeting still has its Slack climate counted, and the route serves it beside the two counts. */}
          {thin && report.insufficient ? (
            <>
              <dt>Slack</dt>
              <dd>{slackBasisText(report.insufficient.slack)}</dd>
            </>
          ) : null}
          {leftOutSentence(report.coverage) ? (
            <>
              <dt>Left out for size</dt>
              <dd>{leftOutSentence(report.coverage)}</dd>
            </>
          ) : null}
          {!thin ? (
            <>
              <dt>Compared with</dt>
              <dd>{report.reviewing ? <Link to={PATHS.executiveReport(report.reviewing.id)} className="hit-area">{reportName(report.reviewing)}</Link> : 'No earlier report with themes'}</dd>
            </>
          ) : null}
        </dl>
        {!thin && report.pulseNote ? (
          <div className="executive-pulse-note">
            <h3 className="executive-subheading">Pulse note</h3>
            <p>{report.pulseNote}</p>
          </div>
        ) : null}
      </section>

      {!thin ? <ThemesTable report={report} /> : null}
      {!thin ? <SuggestedActions report={report} /> : null}
      {!thin ? <SinceLastReport report={report} /> : null}

      {/* R4 — the earlier reports. Why keyed by the report shown (the stage 6 review; 06-modules §5.3 R3: never a subset
          presented as the whole): when a report being written lands under the same address, the list is read again, so it
          holds the new report — marked as the one shown — and its extent counts it. */}
      <EarlierReports key={report.id} shownId={report.id} />
    </>
  );
}

const KIND_RANK = (kind: ExecutiveThemeDto['kind']): number => EXECUTIVE_THEME_KINDS.indexOf(kind);
const TREND_RANK = (trend: ExecutiveThemeDto['trend']): number => EXECUTIVE_TRENDS.indexOf(trend);
const CONFIDENCE_RANK = (confidence: ExecutiveThemeDto['confidence']): number => EXECUTIVE_CONFIDENCES.indexOf(confidence);

/** The themes (design §2.13): kind · title · projects · confidence · trend · evidence, every column sortable, in the URL under `themes_`. */
function ThemesTable({ report }: { report: ExecutiveReportDto }) {
  const rows = useMemo(() => report.themes.map((theme, index) => ({ ...theme, key: `${report.id}-${index}` })), [report]);
  const columns = useMemo<DataTableColumn<ExecutiveThemeDto & { key: string }>[]>(() => [
    { key: 'title', header: 'Theme', card: 'title', sortValue: (row) => row.title, cell: (row) => <span className="executive-theme-title">{row.title}</span> },
    { key: 'kind', header: 'Kind', type: 'enum', directionWords: { asc: 'themes first', desc: 'opportunities first' }, card: 'meta', fold: 3, sortValue: (row) => KIND_RANK(row.kind), cell: (row) => EXECUTIVE_THEME_KIND_LABELS[row.kind] },
    {
      key: 'projects',
      header: 'Projects',
      fold: 1,
      sortValue: (row) => row.projectNames.join(', '),
      cell: (row) => (row.projectNames.length === 0 ? <span className="muted" role="img" aria-label="No project">{EM_DASH}</span> : <span className="cluster">{row.projectNames.map((projectName) => <Chip key={projectName} icon="folder">{projectName}</Chip>)}</span>),
    },
    { key: 'confidence', header: 'Confidence', type: 'enum', directionWords: { asc: 'highest first', desc: 'lowest first' }, fold: 2, sortValue: (row) => CONFIDENCE_RANK(row.confidence), cell: (row) => EXECUTIVE_CONFIDENCE_LABELS[row.confidence] },
    { key: 'trend', header: 'Trend', type: 'enum', directionWords: { asc: 'first seen first', desc: 'settled first' }, card: 'meta', fold: 4, sortValue: (row) => TREND_RANK(row.trend), cell: (row) => EXECUTIVE_TREND_LABELS[row.trend] },
    {
      key: 'evidence',
      header: 'Evidence',
      type: 'number',
      numeric: true,
      directionWords: { asc: 'least evidence first', desc: 'most evidence first' },
      fold: 5,
      // Why meetings, then people: a theme's weight is the meetings it rests on; the people break a tie.
      sortValue: (row) => row.evidenceMeetings + row.evidenceOwners / 10_000,
      cell: (row) => evidenceText(row.evidenceMeetings, row.evidenceOwners),
    },
  ], []);
  // Why no "at least three people" (the owner's ruling of 2026-10-12, "Remove them everywhere"): the Evidence column says
  // how many meetings and people each theme rests on.
  return (
    <Section id="themes" title="Themes" description={pluralise(rows.length, { zero: 'This report kept no themes.', one: '# theme, risk or decision, with the meetings it rests on.', other: '# themes, risks and decisions, each with the meetings it rests on.' })}>
      <DataTable
        caption="Themes"
        failureNoun="the report’s themes"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        sort={{ defaultSort: { key: 'evidence', dir: 'desc' }, namespace: 'themes' }}
        clientSort
        rowDetail={(row) => <p className="executive-theme-summary">{row.summary}</p>}
        // Why no "three people" here (the owner's ruling of 2026-10-12, "Remove them everywhere"): a theme rests on any number
        // of people's meetings; it is kept when it cites the meetings the report read and names no one.
        empty={{ title: 'No themes in this report', description: 'A theme is kept only when it rests on meetings the report read and names no one.' }}
        footer={
          <div className="audit-footer">
            <span>{pluralise(rows.length, { one: '# theme', other: '# themes' })}</span>
            <LinkButton size="sm" variant="secondary" icon="download" href={`/api/executive/reports/${encodeURIComponent(report.id)}/themes.csv`} download>Export themes (CSV)</LinkButton>
          </div>
        }
      />
    </Section>
  );
}

/**
 * The report's suggested actions — each one written onto the shared list as a matter (design §2.7). Why drawn when there
 * are none (the stage 6 review; 06-modules §5.8: "Empty applies per region, not to the page: a record with no relations of
 * one kind says so in that region"): a region that vanished would read as a page that has no such region at all.
 */
function SuggestedActions({ report }: { report: ExecutiveReportDto }) {
  return (
    <Section
      id="actions"
      title="Suggested actions"
      description={report.actions.length > 0 ? 'Each one is on the shared list of matters, where every executive marks it for themselves.' : undefined}
      actions={<LinkButton to={PATHS.executiveMatters} variant="link" size="sm" trailingIcon="chevron-right">Open the shared list</LinkButton>}
    >
      {report.actions.length === 0 ? (
        <p className="secondary text-sm" data-no-actions="">{NO_SUGGESTED_ACTIONS}</p>
      ) : (
        <ul className="item-list" role="list">
          {report.actions.map((action, index) => (
            <li key={`${report.id}-action-${index}`}>
              <div className="item-list__main">
                <span className="item-list__title">{action.text}</span>
                <span className="item-list__meta">{action.why}</span>
              </div>
              <div className="item-list__aside">
                <PriorityPill priority={action.priority} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * The verdicts on the report this one was compared with (design §2.13 "Since yesterday"). Why "Since the last report":
 * since "Non-overlapping windows" that report may be an earlier part of the same day, and since "Trends skip thin parts"
 * the latest one that kept themes — never necessarily yesterday's.
 */
function SinceLastReport({ report }: { report: ExecutiveReportDto }) {
  const description = report.reviewing ? `Compared with ${reportInSentence(report.reviewing)}.` : 'There is no earlier report with themes to compare with.';
  return (
    <Section id="since" title="Since the last report" description={description}>
      {report.priorReview.length === 0 ? (
        <p className="secondary text-sm">{report.reviewing ? 'The report says nothing of the themes or matters before it.' : 'Its trends are read against nothing earlier.'}</p>
      ) : (
        <ul className="item-list" role="list">
          {report.priorReview.map((review, index) => (
            <li key={`${report.id}-review-${index}`}>
              <div className="item-list__main">
                <span className="item-list__title">{review.title ?? (review.target === 'theme' ? 'A theme of a report no longer kept' : 'A matter since deleted')}</span>
                <span className="item-list__meta">{review.reason}</span>
              </div>
              <div className="item-list__aside">
                <span className="executive-reading"><span className="muted">{review.target === 'theme' ? 'Theme: ' : 'Matter: '}</span>{EXECUTIVE_PRIOR_VERDICT_LABELS[review.verdict]}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** A report's state as the lists say it: Completed, or Skipped with its reason (05-content §6). */
export function ReportStatePill({ status }: { status: ExecutiveReportRowDto['status'] }) {
  // Why "Not written" and no longer "Not enough material" (the owner's ruling of 2026-10-12): no minimum applies.
  return status === 'ready' ? <StatusPill tone="success">Completed</StatusPill> : <StatusPill tone="neutral" icon="minus" title="Not written">Skipped</StatusPill>;
}

/** The earlier reports (R4; design §2.13: day · contributors · meetings · Slack looks · themes), in the URL under `reports_`. */
function EarlierReports({ shownId }: { shownId: string }) {
  const first = useRequest<ExecutiveReportListResponse>((signal) => api.get<ExecutiveReportListResponse>(`/api/executive/reports?limit=${EARLIER_PAGE}`, { signal }), []);
  const pages = useCursorPages<ExecutiveReportRowDto>(first.data as CursorPage<ExecutiveReportRowDto> | null, (before) => api.get<ExecutiveReportListResponse>(`/api/executive/reports?limit=${EARLIER_PAGE}&before=${encodeURIComponent(before)}`));
  const columns = useMemo<DataTableColumn<ExecutiveReportRowDto>[]>(() => [
    {
      key: 'window_end',
      header: 'Report',
      type: 'date',
      card: 'title',
      sortValue: (row) => row.windowEnd,
      cell: (row) => (row.id === shownId ? <span aria-current="page">{reportName(row)}</span> : <Link to={PATHS.executiveReport(row.id)} className="hit-area">{reportName(row)}</Link>),
    },
    { key: 'state', header: 'State', type: 'enum', directionWords: { asc: 'completed first', desc: 'skipped first' }, fold: 3, sortValue: (row) => (row.status === 'ready' ? 0 : 1), cell: (row) => <ReportStatePill status={row.status} /> },
    { key: 'people', header: 'People', type: 'number', numeric: true, sortValue: (row) => row.people, cell: (row) => formatCount(row.people) },
    { key: 'meetings', header: 'Meetings', type: 'number', numeric: true, sortValue: (row) => row.meetings, cell: (row) => formatCount(row.meetings) },
    { key: 'slack_looks', header: 'Slack looks', type: 'number', numeric: true, fold: 1, sortValue: (row) => row.slackLooks, cell: (row) => (row.slackLooks === null ? EM_DASH : formatCount(row.slackLooks)) },
    { key: 'themes', header: 'Themes', type: 'number', numeric: true, fold: 2, sortValue: (row) => row.themes, cell: (row) => (row.themes === null ? EM_DASH : formatCount(row.themes)) },
  ], [shownId]);
  const sortSpec = useMemo(() => ({ defaultSort: { key: 'window_end', dir: 'desc' as const }, namespace: 'reports' }), []);
  const [sort] = useUrlSort(sortOptionsFor(columns, sortSpec));
  const sortedOtherwise = sort.key !== 'window_end' || sort.dir !== 'desc';
  // Why: a sort other than the list's own order covers every report, so the pages that remain load first (04-data §1.2.1).
  // Why not after a failure: the failure is said beneath the table, and the reader's "Load earlier reports" retries it —
  // never a loop of requests against a route that is refusing them.
  const { more, loading, loadAll, error: pageError } = pages;
  useEffect(() => {
    if (sortedOtherwise && more && !loading && pageError === null) loadAll();
  }, [sortedOtherwise, more, loading, pageError, loadAll]);

  return (
    <Section id="earlier-reports" title="Earlier reports" description="Every report written or withheld, each part of a refreshed day a report of its own.">
      <DataTable
        caption="Earlier reports"
        failureNoun="the earlier reports"
        columns={columns}
        rows={pages.rows}
        rowKey={(row) => row.id}
        status={first.status === 'loading' && !first.data ? { kind: 'loading', rowCount: 5 } : first.status === 'error' && first.error && !first.data ? { kind: 'error', failure: first.error, onRetry: first.reload } : { kind: 'ready' }}
        sort={sortSpec}
        clientSort
        rowLabel={(row) => reportName(row)}
        empty={{ title: 'No reports yet', description: 'Reports appear here as they are written.' }}
        footer={
          <div className="audit-footer">
            <span>{sortedOtherwise && pages.more && !pages.error ? LOADING_ALL_REPORTS : reportsExtentText(pages.rows.length, pages.more)}</span>
            {pages.more && (!sortedOtherwise || pages.error) ? <Button size="sm" variant="secondary" onClick={pages.error && sortedOtherwise ? pages.loadAll : pages.loadMore} loading={pages.loading}>Load earlier reports</Button> : null}
          </div>
        }
      />
      {/* Why `failureDetail` and never the server's message raw (CLAUDE.md; 05-content §4): a refusal carries its next step,
          a fault loses the envelope's "Something went wrong" and keeps its reference. */}
      {pages.error ? <p className="field__error" role="alert">We couldn’t load the earlier reports. {failureDetail(pages.error)}</p> : null}
    </Section>
  );
}
